import { useState } from 'react';
import { ArrowLeft, Upload, FileText, Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

const LawPackImport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    region: '',
    description: '',
    source: 'manual',
    file: null as File | null
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
      // Auto-populate some fields based on filename
      const name = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, ' ');
      setFormData(prev => ({ 
        ...prev, 
        name: name,
        version: '1.0'
      }));
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.region) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return;
      }
      setStep(2);
    }
  };

  const handleImport = async () => {
    try {
      // Simulate import process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "Law Pack Imported Successfully",
        description: `${formData.name} has been imported and is now available.`
      });
      
      navigate('/law-packs');
    } catch (error) {
      toast({
        title: "Import Failed",
        description: "Failed to import law pack. Please try again.",
        variant: "destructive"
      });
    }
  };

  const availableRegions = [
    'United Kingdom',
    'European Union',
    'United States',
    'Canada',
    'Australia',
    'New Zealand',
    'Germany',
    'France',
    'Spain',
    'Italy',
    'Netherlands'
  ];


  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <Link to="/law-packs" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Law Packs
          </Link>
          <div>
            <h1 className="heading-section mb-2">Import Law Pack</h1>
            <p className="text-muted-foreground">Import a new compliance law pack for your organization</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              <div className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-xs font-medium">1</div>
              <span className="text-sm font-medium">Basic Information</span>
            </div>
            <div className="w-8 h-px bg-border"></div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              <div className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-xs font-medium">2</div>
              <span className="text-sm font-medium">Review & Import</span>
            </div>
          </div>
        </div>

        {step === 1 && (
          <>
            {/* Import Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card className="glass-panel border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Upload Document
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <Label htmlFor="file-upload" className="cursor-pointer">
                        <span className="text-sm font-medium">Click to upload</span>
                        <span className="text-sm text-muted-foreground block">PDF, DOC, or DOCX files</span>
                      </Label>
                      <Input
                        id="file-upload"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                    {formData.file && (
                      <div className="flex items-center gap-2 text-sm text-green-400">
                        <CheckCircle className="h-4 w-4" />
                        {formData.file.name}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-panel border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Quick Import
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Quick import options are not available yet. Please use manual entry or document upload.
                  </p>
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">Pre-configured law packs will be available in a future update.</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Basic Information Form */}
            <Card className="glass-panel border-0">
              <CardHeader>
                <CardTitle>Law Pack Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Law Pack Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., UK Food Standards Agency - Food Safety"
                      className="glass-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="version">Version *</Label>
                    <Input
                      id="version"
                      value={formData.version}
                      onChange={(e) => handleInputChange('version', e.target.value)}
                      placeholder="e.g., 2025.1"
                      className="glass-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">Region/Jurisdiction *</Label>
                  <Select value={formData.region} onValueChange={(value) => handleInputChange('region', value)}>
                    <SelectTrigger className="glass-input">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent className="glass-panel border-0 bg-background/95 backdrop-blur-sm z-50">
                      {availableRegions.map((region) => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description of the law pack requirements..."
                    className="glass-input min-h-[100px]"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end mt-8">
              <Button className="cta-primary" onClick={handleNext}>
                Continue to Review
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* Review Summary */}
            <Card className="glass-panel border-0 mb-8">
              <CardHeader>
                <CardTitle>Review Law Pack</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Please review the law pack details before importing
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Name</Label>
                      <p className="font-medium">{formData.name}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Version</Label>
                      <p className="font-medium">{formData.version}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Region</Label>
                      <p className="font-medium">{formData.region}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Source</Label>
                      <Badge variant="outline">
                        {formData.file ? 'Document Upload' : 'Manual Entry'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Status</Label>
                      <Badge variant="outline">Active</Badge>
                    </div>
                    {formData.file && (
                      <div>
                        <Label className="text-sm text-muted-foreground">File</Label>
                        <p className="font-medium">{formData.file.name}</p>
                      </div>
                    )}
                  </div>
                </div>
                {formData.description && (
                  <div className="mt-6">
                    <Label className="text-sm text-muted-foreground">Description</Label>
                    <p className="mt-1">{formData.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Warning Notice */}
            <Card className="glass-panel border-0 border-l-4 border-l-orange-500 mb-8">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium mb-2">Important Notice</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      This system provides guidance only. You remain fully responsible for compliance with all applicable food safety regulations.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Please ensure this law pack is appropriate for your specific jurisdiction and business type.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" className="glass-button" onClick={() => setStep(1)}>
                Back to Edit
              </Button>
              <Button className="cta-primary" onClick={handleImport}>
                Import Law Pack
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LawPackImport;